import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';


const PostEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [post, setPost] = useState({
        title: '',
        content: '',
        excerpt: '',
        featured_image: null,
        categories: [],
        status: 'draft',
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        focus_keyword: ''
    });
    const [seoScores, setSeoScores] = useState({
        title: 0,
        description: 0,
        keyword: 0,
        overall: 0
    });
    const [previewImage, setPreviewImage] = useState(null);

    useEffect(() => {
        if (isEditMode) {
            fetchPost();
        }
    }, [id]);

    const fetchPost = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/api/posts/${id}`);
            const postData = response.data;
            
            setPost({
                title: postData.title || '',
                content: postData.content || '',
                excerpt: postData.excerpt || '',
                featured_image: null,
                categories: postData.categories || [],
                status: postData.status || 'draft',
                meta_title: postData.meta_title || '',
                meta_description: postData.meta_description || '',
                meta_keywords: postData.meta_keywords || '',
                focus_keyword: postData.focus_keyword || ''
            });
            
            if (postData.featured_image_url) {
                setPreviewImage(postData.featured_image_url);
            }
        } catch (error) {
            console.error('Error fetching post:', error);
            toast.error('Failed to load post data');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const formData = new FormData();
            
            // Prepare post data
            const postData = {
                ...post,
                meta_description: post.meta_description || post.excerpt || post.content?.substring(0, 160) || '',
                meta_title: post.meta_title || post.title || '',
                meta_keywords: post.meta_keywords || '',
                focus_keyword: post.focus_keyword || ''
            };

            // Add all fields to formData
            Object.keys(postData).forEach(key => {
                if (key === 'categories') {
                    formData.append(key, JSON.stringify(postData[key]));
                } else if (key === 'featured_image') {
                    if (postData[key] instanceof File) {
                        formData.append('featured_image', postData[key]);
                    }
                } else {
                    formData.append(key, postData[key]);
                }
            });

            let response;
            if (isEditMode) {
                response = await api.put(`/api/posts/${id}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                toast.success('Post updated successfully');
            } else {
                response = await api.post('/api/posts', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                toast.success('Post created successfully');
            }

            console.log('Server response:', response.data);
            navigate('/posts');
        } catch (error) {
            console.error('Detailed error in handleSubmit:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            toast.error(error.response?.data?.message || 'Failed to save post');
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setPost(prev => ({
                ...prev,
                featured_image: file
            }));
            
            // Create a local preview URL for the selected file
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
            
            toast.success('Image selected successfully');
        } catch (error) {
            console.error('Error handling image:', error);
            toast.error('Failed to handle image');
        }
    };

    const analyzeSEO = () => {
        // Title analysis
        let titleScore = 0;
        const metaTitle = post.meta_title || post.title || '';
        if (metaTitle.length > 0) titleScore += 25;
        if (metaTitle.length >= 30) titleScore += 25;
        if (metaTitle.length <= 60) titleScore += 25;
        if (post.focus_keyword && metaTitle.toLowerCase().includes(post.focus_keyword.toLowerCase())) titleScore += 25;
        
        // Description analysis
        let descriptionScore = 0;
        const metaDescription = post.meta_description || post.excerpt || '';
        if (metaDescription.length > 0) descriptionScore += 25;
        if (metaDescription.length >= 70) descriptionScore += 25;
        if (metaDescription.length <= 160) descriptionScore += 25;
        if (post.focus_keyword && metaDescription.toLowerCase().includes(post.focus_keyword.toLowerCase())) descriptionScore += 25;
        
        // Keyword analysis
        const keywordScore = analyzeKeywordScore();
        
        // Overall score
        const overall = Math.round((titleScore + descriptionScore + keywordScore) / 3);
        
        setSeoScores({
            title: titleScore,
            description: descriptionScore,
            keyword: keywordScore,
            overall
        });
    };
    
    const analyzeKeywordScore = () => {
        let score = 0;
        const keyword = post.focus_keyword || '';
        const content = post.content || '';
        
        // Keyword length (2-4 words is optimal)
        const wordCount = keyword.split(' ').length;
        if (wordCount >= 2 && wordCount <= 4) score += 25;
        
        // Keyword density (1-3% is optimal)
        const keywordCount = (content.toLowerCase().match(new RegExp(keyword.toLowerCase(), 'g')) || []).length;
        const contentWords = content.split(' ').length;
        const density = (keywordCount / contentWords) * 100;
        
        if (density >= 1 && density <= 3) score += 25;
        
        // Keyword in first paragraph
        const firstParagraph = content.split('</p>')[0];
        if (firstParagraph && firstParagraph.toLowerCase().includes(keyword.toLowerCase())) score += 25;
        
        // Keyword in headings
        if (/<h[1-6][^>]*>.*?{keyword}.*?<\/h[1-6]>/i.test(content)) score += 25;
        
        return score;
    };

    useEffect(() => {
        analyzeSEO();
    }, [post.meta_title, post.meta_description, post.focus_keyword, post.content, post.meta_keywords]);

    return (
        <div className="relative z-10">
            <div className="mb-8">
                <h2 className="text-3xl font-bold text-white mb-8">
                    {isEditMode ? 'Edit Post' : 'Create New Post'}
                </h2>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Title Section */}
                <div className="bg-[#111827] border border-gray-800 rounded-xl p-6">
                    <input
                        type="text"
                        value={post.title}
                        onChange={(e) => setPost({ ...post, title: e.target.value })}
                        placeholder="Post Title"
                        className="w-full bg-[#111827] border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-[#FF3366] focus:ring-1 focus:ring-[#FF3366] transition-all duration-200"
                        required
                    />
                </div>

                {/* Content Editor */}
                <div className="bg-[#111827] border border-gray-800 rounded-xl p-6">
                    <Editor
                        apiKey="z15zjexnbv587g32065wtax3xng9q9m003d60mywklvnmsa2"
                        value={post.content}
                        onEditorChange={(content) => setPost({ ...post, content })}
                        init={{
                            height: 500,
                            menubar: false,
                            plugins: [
                                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                            ],
                            toolbar: 'undo redo | blocks | ' +
                                'bold italic forecolor | alignleft aligncenter ' +
                                'alignright alignjustify | bullist numlist outdent indent | ' +
                                'removeformat | help',
                            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px; background-color: #111827; color: #F9FAFB; }',
                            skin: 'oxide-dark',
                            content_css: 'dark'
                        }}
                    />
                </div>

                {/* Excerpt */}
                <div className="bg-[#111827] border border-gray-800 rounded-xl p-6">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        Excerpt
                    </label>
                    <textarea
                        value={post.excerpt}
                        onChange={(e) => setPost({ ...post, excerpt: e.target.value })}
                        placeholder="Brief summary of the post"
                        className="w-full bg-[#111827] border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 h-24 focus:border-[#FF3366] focus:ring-1 focus:ring-[#FF3366] transition-all duration-200"
                    />
                </div>

                {/* SEO Analysis with Integrated Input Fields */}
                <div className="bg-[#111827] border border-gray-800 rounded-xl p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-medium text-white">SEO Analysis</h3>
                        <div className="flex items-center">
                            <span className={`text-sm font-medium ${
                                seoScores.overall >= 70 ? 'text-green-400' :
                                seoScores.overall >= 40 ? 'text-yellow-400' : 'text-red-400'
                            }`}>{seoScores.overall}% Score</span>
                        </div>
                    </div>
                    
                    <div className="space-y-6">
                        {/* Focus Keyword */}
                        <div className="mb-6">
                            <label htmlFor="focus_keyword" className="block text-sm font-medium text-gray-300 mb-2">
                                Focus Keyword
                            </label>
                            <input
                                type="text"
                                id="focus_keyword"
                                value={post.focus_keyword}
                                onChange={(e) => setPost({ ...post, focus_keyword: e.target.value })}
                                className="w-full bg-[#111827] border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-[#FF3366] focus:ring-1 focus:ring-[#FF3366] transition-all duration-200"
                                placeholder="Main keyword for this post"
                            />
                            <div className="mt-2 flex items-center">
                                <div className="w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
                                    <div 
                                        className={`h-full rounded-full ${
                                            seoScores.keyword >= 70 ? 'bg-green-500' :
                                            seoScores.keyword >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                                        }`}
                                        style={{ width: `${seoScores.keyword}%` }}
                                    />
                                </div>
                                <span className="ml-2 text-sm text-gray-400">{seoScores.keyword}% Keyword Score</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                Use 2-4 word phrases for best results
                            </p>
                        </div>
                        
                        {/* Meta Title */}
                        <div className="mb-6">
                            <label htmlFor="meta_title" className="block text-sm font-medium text-gray-300 mb-2">
                                Meta Title
                            </label>
                            <input
                                type="text"
                                id="meta_title"
                                value={post.meta_title}
                                onChange={(e) => setPost({ ...post, meta_title: e.target.value })}
                                className="w-full bg-[#111827] border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-[#FF3366] focus:ring-1 focus:ring-[#FF3366] transition-all duration-200"
                                placeholder="SEO title (leave empty to use post title)"
                            />
                            <div className="mt-2 flex items-center">
                                <div className="w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
                                    <div 
                                        className={`h-full rounded-full ${
                                            seoScores.title >= 70 ? 'bg-green-500' :
                                            seoScores.title >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                                        }`}
                                        style={{ width: `${seoScores.title}%` }}
                                    />
                                </div>
                                <span className="ml-2 text-sm text-gray-400">{seoScores.title}% Title Score</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                {post.meta_title.length} characters (Recommended: 50-60)
                            </p>
                        </div>
                        
                        {/* Meta Description */}
                        <div className="mb-6">
                            <label htmlFor="meta_description" className="block text-sm font-medium text-gray-300 mb-2">
                                Meta Description
                            </label>
                            <textarea
                                id="meta_description"
                                value={post.meta_description}
                                onChange={(e) => setPost({ ...post, meta_description: e.target.value })}
                                rows="3"
                                className="w-full bg-[#111827] border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-[#FF3366] focus:ring-1 focus:ring-[#FF3366] transition-all duration-200"
                                placeholder="SEO description (leave empty to use excerpt)"
                            ></textarea>
                            <div className="mt-2 flex items-center">
                                <div className="w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
                                    <div 
                                        className={`h-full rounded-full ${
                                            seoScores.description >= 70 ? 'bg-green-500' :
                                            seoScores.description >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                                        }`}
                                        style={{ width: `${seoScores.description}%` }}
                                    />
                                </div>
                                <span className="ml-2 text-sm text-gray-400">{seoScores.description}% Description Score</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                {post.meta_description.length} characters (Recommended: 150-160)
                            </p>
                        </div>
                        
                        {/* Meta Keywords */}
                        <div className="mb-6">
                            <label htmlFor="meta_keywords" className="block text-sm font-medium text-gray-300 mb-2">
                                Meta Keywords
                            </label>
                            <input
                                type="text"
                                id="meta_keywords"
                                value={post.meta_keywords}
                                onChange={(e) => setPost({ ...post, meta_keywords: e.target.value })}
                                className="w-full bg-[#111827] border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-[#FF3366] focus:ring-1 focus:ring-[#FF3366] transition-all duration-200"
                                placeholder="Comma-separated keywords"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Separate keywords with commas (e.g., blog, news, technology)
                            </p>
                        </div>
                        
                        {/* Overall SEO Score */}
                        <div className="mt-6 pt-4 border-t border-gray-700">
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-300">Overall SEO Score</span>
                                <div className="flex items-center">
                                    <div className="w-24 h-3 bg-gray-700 rounded-full overflow-hidden">
                                        <div 
                                            className={`h-full rounded-full ${
                                                seoScores.overall >= 70 ? 'bg-green-500' :
                                                seoScores.overall >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                                            }`}
                                            style={{ width: `${seoScores.overall}%` }}
                                        />
                                    </div>
                                    <span className="ml-2 text-sm font-medium text-gray-300">{seoScores.overall}%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Featured Image */}
                <div className="bg-[#111827] border border-gray-800 rounded-xl p-6">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        Featured Image
                    </label>
                    <input
                        type="file"
                        onChange={handleImageUpload}
                        accept="image/*"
                        className="w-full bg-[#111827] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-[#FF3366] focus:ring-1 focus:ring-[#FF3366] transition-all duration-200"
                    />
                    {(post.featured_image || imagePreview) && (
                        <img
                            id="image-preview"
                            src={
                                imagePreview ||
                                (post.featured_image instanceof File 
                                    ? URL.createObjectURL(post.featured_image)
                                    : post.featured_image.includes('cloudinary.com')
                                        ? post.featured_image
                                        : `https://res.cloudinary.com/dppyg21xf/image/upload/${post.featured_image}`)
                            }
                            alt="Preview"
                            className="mt-4 max-h-48 rounded-lg"
                            onError={(e) => {
                                console.error('Image preview error:', e);
                                e.target.src = '';
                            }}
                        />
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-4">
                    <button
                        type="button"
                        onClick={() => setPost({ ...post, status: 'draft' })}
                        className="px-6 py-3 bg-gray-800 text-gray-300 rounded-xl hover:text-white transition-all duration-200 font-medium"
                    >
                        Save as Draft
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        onClick={() => setPost({ ...post, status: 'published' })}
                        className="px-6 py-3 bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white rounded-xl hover:from-[#FF3366]/90 hover:to-[#FF6B4D]/90 transition-all duration-200 font-medium disabled:opacity-50"
                    >
                        {loading ? 'Saving...' : 'Publish'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PostEditor; 